import { motion, AnimatePresence } from 'framer-motion';
import { FiMenu, FiX, FiSun, FiMoon } from 'react-icons/fi';
import signatureMark from '../../assets/Finalized Logo/without background/logo ( only sign) without bg.png';

export default function Navbar({ mobileMenuOpen, setMobileMenuOpen, scrolled, activeSection, darkMode, toggleDarkMode, scrollToSection, navLinks }) {
  return (
        <motion.nav
          className={`fixed top-0 left-0 right-0 z-50 backdrop-blur-md transition-all duration-300 ${scrolled
            ? "bg-white/80 dark:bg-gray-900/80 border-b border-white/10 dark:border-gray-800 h-16 shadow-sm"
            : "bg-transparent dark:bg-transparent h-20"
            }`}
          initial={{ y: -100 }}
          animate={{ y: 0 }}
          transition={{ duration: 0.5, type: "spring" }}
        >
          <div className="mx-auto flex h-full max-w-7xl items-center justify-between px-4 sm:px-6 md:px-10">
            <motion.button
              type="button"
              aria-label="Shambhavi Singh — Home"
              onClick={() => scrollToSection("home")}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="flex items-center rounded-full p-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-4 dark:focus-visible:ring-offset-gray-900"
            >
              <img src={signatureMark} alt="" className="h-12 w-12 object-contain drop-shadow-[0_1px_2px_rgba(15,23,42,0.16)] md:h-14 md:w-14" />
            </motion.button>

            <div className="flex items-center gap-4">
              {/* Desktop Navigation */}
              <ul className="hidden md:flex space-x-6 items-center">
                {navLinks.map((link) => (
                  <li key={link.id} className="relative">
                    <button
                      onClick={() => scrollToSection(link.id)}
                      className={`px-2 py-1 text-sm font-medium transition ${activeSection === link.id
                        ? "text-indigo-600 dark:text-indigo-400"
                        : "text-gray-700 dark:text-gray-300 hover:text-indigo-500 dark:hover:text-indigo-400"
                        }`}
                    >
                      {link.label}
                      {activeSection === link.id && (
                        <motion.span
                          className="absolute bottom-0 left-0 w-full h-0.5 bg-indigo-500 dark:bg-indigo-400"
                          layoutId="navUnderline"
                          initial={false}
                          transition={{
                            type: "spring",
                            stiffness: 500,
                            damping: 30
                          }}
                        />
                      )}
                    </button>
                  </li>
                ))}

                {/* Theme Toggle Button */}
                <motion.button
                  onClick={toggleDarkMode}
                  className="p-2 rounded-full bg-white/30 dark:bg-gray-800/30 backdrop-blur-md border border-gray-200 dark:border-gray-700"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  aria-label="Toggle dark mode"
                  title={darkMode ? "Switch to light mode" : "Switch to dark mode"}
                >
                  <motion.div
                    animate={darkMode ? { rotate: 360 } : { rotate: 0 }}
                    transition={{ duration: 0.5, type: "spring" }}
                  >
                    {darkMode ? (
                      <FiSun className="text-white" size={18} />
                    ) : (
                      <FiMoon className="text-indigo-700" size={18} />
                    )}
                  </motion.div>
                </motion.button>
              </ul>

              {/* Mobile menu button */}
              <div className="md:hidden flex items-center gap-2">
                <motion.button
                  onClick={toggleDarkMode}
                  className="p-2 rounded-full bg-white/30 dark:bg-gray-800/30 backdrop-blur-md border border-gray-200 dark:border-gray-700"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  aria-label="Toggle dark mode"
                >
                  {darkMode ? (
                    <FiSun className="text-white" size={18} />
                  ) : (
                    <FiMoon className="text-indigo-700" size={18} />
                  )}
                </motion.button>

                <button
                  className="p-2 text-gray-700 dark:text-gray-300"
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  aria-label={mobileMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
                  aria-expanded={mobileMenuOpen}
                  aria-controls="mobile-navigation"
                >
                  {mobileMenuOpen ? <FiX size={24} /> : <FiMenu size={24} />}
                </button>
              </div>
            </div>
          </div>

          {/* Mobile Menu */}
          <AnimatePresence>
            {mobileMenuOpen && (
              <motion.div
                id="mobile-navigation"
                aria-label="Mobile navigation"
                role="navigation"
                className="max-h-[calc(100dvh-4rem)] overflow-y-auto bg-white/95 shadow-lg dark:bg-gray-900/95 md:hidden"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3, ease: "easeInOut" }}
              >
                <ul className="px-6 py-4 space-y-4">
                  {navLinks.map((link) => (
                    <motion.li
                      key={link.id}
                      initial={{ x: -20, opacity: 0 }}
                      animate={{ x: 0, opacity: 1 }}
                      transition={{
                        delay: 0.1 * navLinks.indexOf(link),
                        type: "spring",
                        stiffness: 300
                      }}
                    >
                      <button
                        onClick={() => scrollToSection(link.id)}
                        className={`w-full text-left px-2 py-1 font-medium flex items-center ${activeSection === link.id
                          ? "text-indigo-600 dark:text-indigo-400"
                          : "text-gray-700 dark:text-gray-300"
                          }`}
                      >
                        {link.label}
                        {activeSection === link.id && (
                          <motion.span
                            className="ml-2 w-2 h-2 bg-indigo-500 dark:bg-indigo-400 rounded-full inline-block"
                            layoutId="mobileNavIndicator"
                            initial={false}
                          />
                        )}
                      </button>
                    </motion.li>
                  ))}
                </ul>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.nav>
  );
}
